// ONLY minimal additions + fixes applied on your original code

import React, { useEffect } from "react";
import { Box, Paper, Typography, CircularProgress, Grid, Divider } from "@mui/material";

import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import useAutomationHealthQuery from "../../useQuery/useAutomationHealthQuery";
import useAutomationPerformanceQuery from "../../useQuery/useAutomationPerformanceQuery";

/* ============================= */
/* CARD COMPONENT                */
/* ============================= */

const SectionCard = ({ children }) => (
  <Paper sx={{ p: 3, borderRadius: 3, height: "100%" }}>{children}</Paper>
);

const formatDate = (value) => {
  const d = new Date(value);
  return `${d.getDate()} ${d.toLocaleString("default", { month: "short" })}`;
};

/* ============================= */
/* GRADE ENGINE                  */
/* ============================= */

const calculateGrade = (maturity, execution, reliability) => {
  if (execution === 0) return "F";
  if (maturity >= 90 && execution >= 90 && reliability >= 99) return "A";
  if (maturity >= 70 && execution >= 75 && reliability >= 97) return "B";
  if (maturity >= 40 && execution >= 50 && reliability >= 95) return "C";
  return "D";
};

/* ============================= */
/* MAIN COMPONENT                */
/* ============================= */

const AutomationHealthSection = () => {
  const {
    mutate: fetchHealth,
    data: healthData,
    isPending: healthLoading,
  } = useAutomationHealthQuery();

  const {
    mutate: fetchPerformance,
    data: performanceData,
    isPending: perfLoading,
  } = useAutomationPerformanceQuery();

  useEffect(() => {
    fetchHealth();
    fetchPerformance();
  }, []);

  if (healthLoading || perfLoading) {
    return (
      <Box mt={6} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  const health = healthData?.data || {};
  const performance = performanceData?.data || {};

  const inventory = health.inventory || {};
  const utilization = health.utilization || {};
  const stability = health.stability || {};
  const reliabilityData = health.reliability || {};
  const dailyMetrics = performance.dailyMetrics || [];

  // ✅ ADDED (no removal)
  const activeTests = inventory.activeTests || 0;
  const deletedTests = inventory.deletedTests || 0;

  // ❗ keep original variable
  const total = activeTests || inventory.totalTests || 0;

  const totalTests = inventory.totalTests || 0; // for display only

  const mature = inventory.matureTests || 0;
  const executed = utilization.executedTests || 0;

  const recentlyFailed = stability.recentlyFailedCount || 0;
  const persistentFailed = stability.persistentFailCount || 0;
  const flaky = reliabilityData.flakyCount || 0;

  const rawCaseTypes = inventory.caseTypes || {};

  const caseTypes = Object.entries(rawCaseTypes).reduce((acc, [key, value]) => {
    const type = key === "UNKNOWN" || key === null ? "WEB" : key;
    acc[type] = (acc[type] || 0) + value;
    return acc;
  }, {});

  // ✅ FIXED CALCULATION (only change)
  const maturityPct = total > 0 ? (mature / total) * 100 : 0;
  const executionPct = total > 0 ? (executed / total) * 100 : 0;

  const passed = Math.max(executed - recentlyFailed, 0);
  const reliabilityPct = executed > 0 ? (passed / executed) * 100 : 0;

  const regressionProtection =
    total > 0
      ? (mature / total) * (executed / total) * (executed > 0 ? passed / executed : 0) * 100
      : 0;

  const grade = calculateGrade(maturityPct, executionPct, reliabilityPct);

  return (
    <Box sx={{ width: "100%", mt: 4 }}>
      <Grid container spacing={3} mb={3} alignItems="stretch">
        {/* MATURITY */}
        <Grid item xs={12} md={4}>
          <SectionCard>
            <Typography variant="h4" fontWeight={600}>
              {maturityPct.toFixed(1)}% ({mature}/{total})
            </Typography>
            <Typography variant="subtitle2">Maturity</Typography>

            {/* ✅ ADDED LINE (only addition) */}
            <Typography variant="caption" color="text.secondary">
              Active: {activeTests} | Deleted: {deletedTests} | Total: {totalTests}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Tests that have passed at least 5 consecutive executions at least once.
            </Typography>

            {Object.entries(caseTypes).map(([type, count]) => (
              <Typography key={type} variant="caption" display="block">
                {type}: {count}
              </Typography>
            ))}
          </SectionCard>
        </Grid>

        {/* GRADE */}
        <Grid item xs={12} md={4}>
          <SectionCard>
            <Box textAlign="center">
              <Typography variant="h1" fontWeight={700}>
                {grade}
              </Typography>

              <Typography variant="h4" fontWeight={600}>
                {regressionProtection.toFixed(1)}%
              </Typography>

              <Typography variant="subtitle1">Overall Automation Grade</Typography>

              <Typography variant="body2" color="text.secondary" mt={1}>
                Based on Maturity, Execution Coverage and Reliability.
              </Typography>
            </Box>
          </SectionCard>
        </Grid>

        {/* EXECUTION */}
        <Grid item xs={12} md={4}>
          <SectionCard>
            <Typography variant="h4" fontWeight={600}>
              {executionPct.toFixed(1)}% ({executed}/{total})
            </Typography>
            <Typography variant="subtitle2">Execution Coverage</Typography>
            <Typography variant="body2" color="text.secondary">
              Percentage of automation executed in the last 7 days.
            </Typography>
          </SectionCard>
        </Grid>
      </Grid>

      {/* REST UNCHANGED */}

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12}>
          <SectionCard>
            <Typography variant="h5" fontWeight={600}>
              Regression Protection Index
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={2}>
              Measures how much of your automation truly protects releases. Calculated as: Maturity
              × Execution Coverage × Reliability.
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ height: 250 }}>
              <ResponsiveContainer>
                <LineChart data={dailyMetrics}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={formatDate} />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="coveragePercentage"
                    name="Execution Coverage %"
                    stroke="#1976d2"
                    strokeWidth={3}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <SectionCard>
            <Typography variant="h4">{flaky}</Typography>
            <Typography variant="subtitle2">Flaky Tests</Typography>
            <Typography variant="body2" color="text.secondary">
              Tests that flipped pass/fail at least 3 times in recent executions.
            </Typography>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <SectionCard>
            <Typography variant="h4">{persistentFailed}</Typography>
            <Typography variant="subtitle2">Persistent Failures</Typography>
            <Typography variant="body2" color="text.secondary">
              Tests failing repeatedly across executions.
            </Typography>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <SectionCard>
            <Typography variant="h4">{recentlyFailed}</Typography>
            <Typography variant="subtitle2">Recently Failed</Typography>
            <Typography variant="body2" color="text.secondary">
              Tests that failed within the last 7 days.
            </Typography>
          </SectionCard>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SectionCard>
            <Typography variant="subtitle1" mb={2}>
              Automation Growth
            </Typography>

            <Box sx={{ height: 250 }}>
              <ResponsiveContainer>
                <LineChart data={dailyMetrics}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={formatDate} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="cumulativeTests"
                    name="Total Tests"
                    stroke="#1976d2"
                    strokeWidth={3}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard>
            <Typography variant="subtitle1" mb={2}>
              Daily Automation Productivity
            </Typography>

            <Box sx={{ height: 250 }}>
              <ResponsiveContainer>
                <LineChart data={dailyMetrics}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={formatDate} />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="testsCreated"
                    name="Tests Created Per Day"
                    stroke="#1976d2"
                    strokeWidth={3}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AutomationHealthSection;
